
import { useEffect, useRef, useState } from 'react';

export default function HowItWorks() {
  const [isVisible, setIsVisible] = useState(false);
  const [kineticIndex, setKineticIndex] = useState(0);
  const sectionRef = useRef<HTMLDivElement>(null);

  const kineticPhrases = ['Three Steps', 'Sixty Seconds', 'Filed'];

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.3 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setKineticIndex((prev) => (prev + 1) % kineticPhrases.length);
    }, 2000);
    return () => clearInterval(interval);
  }, []);

  const steps = [
    {
      number: '1',
      title: 'Speak or Upload',
      description: 'Dictate your document or upload a pleading you need to respond to.',
      delay: '0ms',
    },
    {
      number: '2',
      title: 'Tell Us What You Need',
      description: 'Plain English. "Motion to compel discovery" or "Opposition on standing."',
      delay: '200ms',
    },
    {
      number: '3',
      title: 'Download and File',
      description: 'Court-ready Word doc. Your letterhead. Proper formatting. Done.',
      delay: '400ms',
    },
  ];

  return (
    <section ref={sectionRef} className="relative py-32 bg-[#09090b]">
      <div className="max-w-[1200px] mx-auto px-8">
        <div
          className={`text-center mb-6 transition-all duration-700 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <div className="h-[70px] sm:h-[80px] lg:h-[90px] overflow-hidden relative flex items-center justify-center">
            {kineticPhrases.map((phrase, index) => (
              <h2
                key={phrase}
                className={`text-white text-[36px] sm:text-[48px] lg:text-[56px] font-bold leading-tight absolute transition-all duration-500 ease-in-out ${
                  kineticIndex === index
                    ? 'opacity-100 translate-y-0 scale-100'
                    : kineticIndex === (index + 1) % kineticPhrases.length
                      ? 'opacity-0 -translate-y-12 scale-95'
                      : 'opacity-0 translate-y-12 scale-95'
                }`}
                style={{
                  textShadow: kineticIndex === index ? '0 0 40px rgba(6, 182, 212, 0.4), 0 0 80px rgba(6, 182, 212, 0.2)' : 'none'
                }}
              >
                <span className={`${kineticIndex === index ? 'text-[#06b6d4]' : 'text-white'} transition-colors duration-500`}>
                  {phrase}
                </span>
              </h2>
            ))}
          </div>
        </div>

        <p
          className={`text-[#71717a] text-[18px] text-center mb-20 transition-all duration-700 delay-200 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          From idea to filing in under a minute
        </p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
          {steps.map((step, index) => (
            <div
              key={index}
              className={`relative text-center transition-all duration-700 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
              style={{ transitionDelay: step.delay }}
            >
              <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-[#06b6d4] to-[#0891b2] rounded-full mb-6 text-white text-[32px] font-bold shadow-[0_0_30px_rgba(6,182,212,0.4)]">
                {step.number}
              </div>
              <h3 className="text-white text-[24px] font-bold mb-4">{step.title}</h3>
              <p className="text-[#a1a1aa] text-[17px] leading-relaxed">{step.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
